import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { CalendarDays, CheckCircle2, FileSpreadsheet, Loader2, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const num = (n) =>
  n === null || n === undefined ? '-' : Number(n).toLocaleString('es-AR', { maximumFractionDigits: 2 });

const fechaCorta = (f) => (f ? f.split('-').reverse().slice(0, 2).join('/') : '-');

const Stat = ({ label, value, testid }) => (
  <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3" data-testid={testid}>
    <p className="text-xs uppercase tracking-wide text-emerald-600 dark:text-emerald-400">{label}</p>
    <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
  </div>
);

export default function GrillaSemanal() {
  const [semanas, setSemanas] = useState([]);
  const [preview, setPreview] = useState(null);
  const [archivo, setArchivo] = useState(null);
  const [lunes, setLunes] = useState('');
  const [ratio, setRatio] = useState(6);
  const [cargando, setCargando] = useState(false);
  const [importando, setImportando] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const traerSemanas = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/grilla/semanas`, { headers: auth() });
      setSemanas(r.data.semanas || []);
    } catch {
      toast.error('No pude traer las semanas importadas');
    }
  }, []);

  useEffect(() => {
    traerSemanas();
  }, [traerSemanas]);

  const analizar = async (file) => {
    if (!file) return;
    setArchivo(file);
    setPreview(null);
    setCargando(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('ratio', ratio);
    if (lunes) fd.append('fecha_desde', lunes);
    try {
      const r = await axios.post(`${API}/api/grilla/preview`, fd, { headers: auth() });
      setPreview(r.data);
      toast.success(`Leí ${r.data.resumen.total} torneos`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No pude leer el Excel');
      setArchivo(null);
    } finally {
      setCargando(false);
    }
  };

  const importar = async () => {
    setImportando(true);
    const fd = new FormData();
    fd.append('file', archivo);
    fd.append('ratio', ratio);
    if (lunes) fd.append('fecha_desde', lunes);
    try {
      const r = await axios.post(`${API}/api/grilla/importar`, fd, { headers: auth() });
      toast.success(`Grilla activa: ${r.data.resumen.total} torneos cargados`);
      setPreview(null);
      setArchivo(null);
      if (inputRef.current) inputRef.current.value = '';
      traerSemanas();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No pude importar la grilla');
    } finally {
      setImportando(false);
    }
  };

  const borrar = async (id) => {
    if (!window.confirm('¿Borrar esta semana y todos sus torneos?')) return;
    try {
      await axios.delete(`${API}/api/grilla/semanas/${id}`, { headers: auth() });
      toast.success('Semana borrada');
      traerSemanas();
    } catch {
      toast.error('No pude borrar la semana');
    }
  };

  const r = preview?.resumen;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8" data-testid="grilla-semanal-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600 dark:text-emerald-400">
            Torneos MTT · Unión
          </p>
          <h1 className="mt-1 text-4xl font-bold text-gray-900 dark:text-gray-100 sm:text-5xl">Grilla semanal</h1>
          <p className="mt-2 max-w-2xl text-base text-gray-600 dark:text-gray-400">
            Subí el Excel del organizador. Convierto los buy-in de dólares a PP y el bot
            sirve la grilla del día cuando un club escribe <code className="text-emerald-600 dark:text-emerald-300 font-semibold">/grilla</code>.
          </p>
        </div>
        <div className="rounded-xl border border-emerald-300 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 px-4 py-2 text-sm text-emerald-700 dark:text-emerald-200">
          Ratio actual <span className="font-bold">1 USD = {ratio} PP</span>
        </div>
      </header>

      <section
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          analizar(e.dataTransfer.files?.[0]);
        }}
        className={`rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${
          drag ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10' : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50'
        }`}
        data-testid="grilla-dropzone"
      >
        {cargando ? (
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-emerald-600" />
        ) : (
          <FileSpreadsheet className="mx-auto h-10 w-10 text-emerald-600" />
        )}
        <p className="mt-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {archivo ? archivo.name : 'Arrastrá el Excel de la grilla acá'}
        </p>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Formato .xlsx del organizador (PPST)</p>

        <div className="mt-6 flex flex-wrap items-end justify-center gap-4">
          <label className="text-left text-sm text-gray-700 dark:text-gray-300">
            <span className="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Lunes de la semana (opcional)
            </span>
            <input
              type="date"
              value={lunes}
              onChange={(e) => setLunes(e.target.value)}
              className="mt-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-gray-900 dark:text-gray-100"
              data-testid="grilla-input-lunes"
            />
          </label>
          <label className="text-left text-sm text-gray-700 dark:text-gray-300">
            <span className="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              1 USD = ? PP
            </span>
            <input
              type="number"
              min="1"
              step="0.5"
              value={ratio}
              onChange={(e) => setRatio(Number(e.target.value) || 6)}
              className="mt-1 w-24 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-gray-900 dark:text-gray-100"
              data-testid="grilla-input-ratio"
            />
          </label>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={(e) => analizar(e.target.files?.[0])}
            data-testid="grilla-file-input"
          />
          <button
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-6 py-3 font-semibold text-white shadow-lg transition-transform hover:scale-[1.03] hover:bg-emerald-700"
            data-testid="grilla-btn-elegir"
          >
            <Upload className="h-4 w-4" /> Elegir archivo
          </button>
        </div>
      </section>

      {r && (
        <section className="space-y-5 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm" data-testid="grilla-preview">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Vista previa · {fechaCorta(r.fecha_desde)} al {fechaCorta(r.fecha_hasta)}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Revisá que esté todo bien antes de activarla. Nada se guarda hasta que confirmes.
              </p>
            </div>
            <button
              onClick={importar}
              disabled={importando}
              className="inline-flex items-center gap-2 rounded-full bg-gray-900 dark:bg-white px-6 py-3 font-semibold text-white dark:text-gray-900 shadow-lg transition-transform hover:scale-[1.03] disabled:opacity-50"
              data-testid="grilla-btn-importar"
            >
              {importando ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Activar esta grilla
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            <Stat label="Torneos" value={r.total} testid="grilla-stat-total" />
            <Stat label="KO / Bounty" value={r.ko} testid="grilla-stat-ko" />
            <Stat label="PKO" value={r.pko} testid="grilla-stat-pko" />
            <Stat label="Con add-on" value={r.con_addon} testid="grilla-stat-addon" />
            <Stat label="Satélites" value={r.satelites} testid="grilla-stat-sat" />
          </div>

          <div className="flex flex-wrap gap-2">
            {Object.entries(r.por_dia).map(([f, n]) => (
              <span key={f} className="rounded-full border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 px-3 py-1 text-sm text-gray-700 dark:text-gray-300">
                {fechaCorta(f)} · <span className="font-semibold text-gray-900 dark:text-gray-100">{n}</span>
              </span>
            ))}
          </div>

          {preview.avisos?.length > 0 && (
            <div className="rounded-xl border border-amber-300 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-4 text-sm text-amber-800 dark:text-amber-200">
              <p className="font-semibold">Filas que no pude leer:</p>
              <ul className="mt-1 list-disc pl-5">
                {preview.avisos.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="py-2 pr-4">Día</th>
                  <th className="py-2 pr-4">Hora</th>
                  <th className="py-2 pr-4">Torneo</th>
                  <th className="py-2 pr-4">Tipo</th>
                  <th className="py-2 pr-4">Entrada PP</th>
                  <th className="py-2 pr-4">GTD PP</th>
                  <th className="py-2 pr-4">Add-on</th>
                  <th className="py-2">Estructura</th>
                </tr>
              </thead>
              <tbody className="text-gray-700 dark:text-gray-300">
                {preview.muestra.map((e, i) => (
                  <tr key={i} className="border-t border-gray-100 dark:border-gray-700">
                    <td className="py-2 pr-4">{e.dia}</td>
                    <td className="py-2 pr-4 font-mono text-gray-900 dark:text-gray-100">{e.hora}</td>
                    <td className="py-2 pr-4 font-semibold text-gray-900 dark:text-gray-100">{e.nombre}</td>
                    <td className="py-2 pr-4">
                      {e.es_pko ? 'PKO' : e.es_ko ? 'KO' : e.tiene_rebuy ? 'Rebuy' : 'Freezeout'}
                      {e.variante !== 'NLH' ? ` · ${e.variante}` : ''}
                    </td>
                    <td className="py-2 pr-4">{num(e.buyin_pp)}</td>
                    <td className="py-2 pr-4">{num(e.gtd_pp)}</td>
                    <td className="py-2 pr-4">
                      {e.tiene_addon ? `${num(e.addon_pp)} ${e.addon_mult || ''}` : '—'}
                    </td>
                    <td className="py-2">{e.estructura || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Muestra de las primeras {preview.muestra.length} filas.</p>
          </div>
        </section>
      )}

      <section className="space-y-3" data-testid="grilla-semanas">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
          <CalendarDays className="h-5 w-5 text-emerald-600" /> Semanas importadas
        </h2>
        {semanas.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Todavía no importaste ninguna grilla.</p>
        ) : (
          <div className="space-y-2">
            {semanas.map((s) => (
              <div
                key={s.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 shadow-sm"
                data-testid={`grilla-semana-${s.id}`}
              >
                <div>
                  <p className="font-semibold text-gray-900 dark:text-gray-100">
                    {fechaCorta(s.fecha_desde)} al {fechaCorta(s.fecha_hasta)}
                    {s.activa && (
                      <span className="ml-2 rounded-full bg-emerald-100 dark:bg-emerald-500/20 px-2 py-0.5 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                        ACTIVA
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {s.total_eventos} torneos · 1 USD = {s.ratio} PP · {s.archivo}
                  </p>
                </div>
                <button
                  onClick={() => borrar(s.id)}
                  className="inline-flex items-center gap-1 rounded-lg border border-red-300 dark:border-red-500/30 px-3 py-2 text-sm text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/10"
                  data-testid={`grilla-btn-borrar-${s.id}`}
                >
                  <Trash2 className="h-4 w-4" /> Borrar
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
